import { useState, useEffect } from 'react'
import { Container, Collapse, CardBody, Card, CardHeader, Button } from 'reactstrap'
import s from './Docs.scss'
import { FaAngleRight, FaAngleDown, FaCertificate, FaTrashAlt, FaFolder } from 'react-icons/fa'
import { Folders } from './Folders'
import { getCompanyId } from '../helpers'
import { useParams } from 'react-router-dom'
import { groupProposalsByClient } from '../utils'
import { sortClientsByAwarded } from 'utils/helpers'
import { useLoading } from 'hooks'
import { makeGetRequest, makeDeleteRequest, makePutRequest } from 'utils/api'
import Dropzone from 'react-dropzone'
import { List } from './List'
import { toDocumentFormData } from '../serializers'
import { useDispatch } from 'react-redux'
import cn from 'classnames'
import { toDocuments, toFolders } from './serializers'
import { MoveFilesModal } from './MoveFilesModal'
import { BsFolderSymlinkFill } from 'react-icons/bs'

const Docs = ({ awardedCompany, dealCompanies, documentsCount }) => {
  const [collapse, setCollapse] = useState('')
  const [proposalsByClient, setProposalsByClient] = useState({})
  const { id } = useParams()
  const [, withLoadingLocks] = useLoading()
  const [folders, setFolders] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const dispatch = useDispatch()
  const [selectedFolder, setSelectedFolder] = useState(null)
  const [documents, setDocuments] = useState([])
  const [selectedCompanyId, setSelectedCompanyId] = useState(null)
  const [showMoveFilesModal, setShowMoveFilesModal] = useState(false)

  useEffect(() => {
    ;(async () => {
      const response = await withLoadingLocks(makeGetRequest('/v1/folder'))
      setFolders(response.filter(folder => folder.visible))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setProposalsByClient(
      groupProposalsByClient(sortClientsByAwarded(dealCompanies, awardedCompany), [], 'name')
    )
  }, [awardedCompany, dealCompanies])

  const toggle = (e, companyId) => {
    const event = e.target.dataset.event
    setCollapse(event)
    setSelectedCompanyId(event ? companyId : null)
  }

  const onDrop = async (files, dealId, companyId, folderId) => {
    try {
      const url = `/v1/deals/${dealId}/documents`
      const deal = await withLoadingLocks(
        makePutRequest(url, toDocumentFormData(companyId, files, folderId, dealId))
      )

      const docs = deal.dealDocuments.filter(document => document.client.id === companyId)

      setDocuments(toDocuments(docs))

      dispatch({
        type: 'FETCH_DEAL_DOCUMENTS_COUNT_REQUESTED',
        payload: {
          dealId
        }
      })
    } catch (e) {
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'Error uploading document',
          level: 'error'
        }
      })
    }
  }

  const toggleMoveFilesModal = () => {
    setShowMoveFilesModal(!showMoveFilesModal)
  }

  const triggerCheckbox = id => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(elem => elem !== id))
    } else {
      setSelectedIds([...selectedIds, id])
    }
  }

  const deleteSelected = async (dealId, companyId) => {
    if (!selectedIds.length) return

    try {
      const url = `/v1/deals/${dealId}/documents?companyId=${companyId}&dealDocumentIds=${selectedIds}`
      await withLoadingLocks(makeDeleteRequest(url))

      dispatch({
        type: 'FETCH_DEAL_DOCUMENTS_COUNT_REQUESTED',
        payload: {
          dealId
        }
      })

      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'Document(s) deleted successfully',
          level: 'success'
        }
      })

      setSelectedIds([])

      setDocuments(
        toDocuments(documents[selectedFolder.id].filter(doc => !selectedIds.includes(doc.id)))
      )
    } catch (e) {
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'Error deleting document(s)',
          level: 'error'
        }
      })
    }
  }

  const moveFiles = async destinationFolder => {
    if (!selectedIds.length) return

    try {
      const url = `/v1/deals/${id}/move-documents`
      await withLoadingLocks(
        makePutRequest(url, {
          ids: selectedIds,
          folderId: destinationFolder.value
        })
      )

      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'Document(s) moved successfully',
          level: 'success'
        }
      })

      setSelectedIds([])

      setDocuments(
        toDocuments(documents[selectedFolder.id].filter(doc => !selectedIds.includes(doc.id)))
      )
      toggleMoveFilesModal()
    } catch (e) {
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'Error moving document(s)',
          level: 'error'
        }
      })
    }
  }

  return (
    <Container className={s.container}>
      <>
        {!selectedFolder &&
          Object.keys(proposalsByClient).map(proposal => {
            const companyId = getCompanyId(proposalsByClient, proposal)

            return (
              <Card className={s.card} key={proposal}>
                <CardHeader onClick={e => toggle(e, companyId)} data-event={proposal}>
                  <span className={s.arrow}>
                    {collapse === proposal ? <FaAngleRight /> : <FaAngleDown />}
                  </span>
                  {+awardedCompany?.value === +companyId && (
                    <FaCertificate className={s.awardIcon} title="Opportunity Awarded" />
                  )}
                  {proposal} ({documentsCount?.[companyId] ?? 0})
                </CardHeader>
                <Collapse isOpen={collapse === proposal}>
                  <CardBody>
                    {collapse === proposal && companyId && (
                      <Folders
                        dealId={id}
                        companyId={companyId}
                        folders={folders}
                        setDocuments={values => setDocuments(toDocuments(values))}
                        documents={documents}
                        setSelectedFolder={setSelectedFolder}
                      />
                    )}
                  </CardBody>
                </Collapse>
              </Card>
            )
          })}

        {selectedFolder && (
          <>
            <div>
              <FaFolder className={s.folderIcon} />{' '}
              <span
                className={cn(s.colorGreen, s.link)}
                onClick={() => {
                  setSelectedFolder(null)
                  setSelectedIds([])
                }}
              >
                Docs
              </span>{' '}
              <FaAngleRight className={s.colorGreen} />{' '}
              <span className={s.colorGreen}>{selectedFolder.name}</span>
            </div>
            {!!selectedIds.length && (
              <>
                <Button
                  onClick={() => deleteSelected(id, selectedCompanyId)}
                  className={s.delete}
                  color="secondary"
                >
                  <FaTrashAlt /> Delete Selected
                </Button>
                <Button onClick={toggleMoveFilesModal} className={s.delete} color="secondary">
                  <BsFolderSymlinkFill /> Move to Folder
                </Button>
              </>
            )}
            <List
              ids={selectedIds}
              triggerCheckbox={triggerCheckbox}
              documents={documents[selectedFolder.id] ?? []}
            />
            <Dropzone onDrop={files => onDrop(files, id, selectedCompanyId, selectedFolder.id)}>
              {({ getRootProps, getInputProps }) => (
                <section>
                  <div {...getRootProps({ className: [s.dropzone] })}>
                    <input {...getInputProps()} />
                    <p>Drag and drop some files here, or click to select files</p>
                  </div>
                </section>
              )}
            </Dropzone>
            {showMoveFilesModal && (
              <MoveFilesModal
                showMoveFilesModal={showMoveFilesModal}
                toggleMoveFilesModal={toggleMoveFilesModal}
                folders={toFolders(folders.filter(s => s.id !== selectedFolder.id))}
                moveFiles={moveFiles}
              />
            )}
          </>
        )}
      </>
    </Container>
  )
}

export default Docs
